import React, { useEffect, useState } from "react";
import { DefaultAndAuthProps } from "model/props";
import PageHeader from "components/pageheader/PageHeader";
import Search from "components/search/Search";
import FormButton from "components/form/FormButton";
import DataTable, { Column } from "components/datatable/DataTable";
import { getLanguageString as _ } from "misc/lang";
import { Paths } from "appConfig";
import ConfirmDialog from "components/confirmdialog/ConfirmDialog";

import cssLayout from "../layout.module.scss";
import css from "./organisationscreen.module.scss";
import { Organisation } from "model/organisation";
import { apiGetAccountList, apiAccountDelete } from "net/requests";
import { Alert, Snackbar } from "@mui/material";

const OrganisationScreen: React.FC<DefaultAndAuthProps> = (props) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    item: any;
  }>({ open: false, item: null });
  const { admin, navigate, globalAppMode } = props;

  const handleCreateNew = () => {
    navigate(Paths.ADD_ORGANISATION);
  };

  const handleDelete = (item: any) => {
    setDeleteDialog({ open: true, item });
  };

  const confirmDelete = async () => {
    if (!admin || !deleteDialog.item) return;

    try {
      await apiAccountDelete(admin, deleteDialog.item.uuid);
      setNotification({
        open: true,
        message: _("organisation", "delete_success"),
        severity: "success",
      });
      setDeleteDialog({ open: false, item: null });
      // Reload the organisations list after successful deletion
      actionLoadOrganisations();
    } catch (error) {
      console.error("Error deleting organisation:", error);
      setNotification({
        open: true,
        message: _("organisation", "delete_error"),
        severity: "error",
      });
      setDeleteDialog({ open: false, item: null });
    }
  };

  const handleRowClick = (item: any) => {
    navigate(Paths.EDIT_ORGANISATION.replace(":id", item.uuid));
  };

  const columns: Column[] = [
    {
      key: "name",
      label:
        globalAppMode === "HEALTH"
          ? _("organisation", "health_organisation_name")
          : _("organisation", "organisation_name"),
    },
    {
      key: "org_number",
      label:
        globalAppMode === "HEALTH"
          ? _("organisation", "health_organisation_number")
          : _("organisation", "organisation_number"),
    },
    {
      key: "location",
      label: _("organisation", "location"),
    },
  ];

  const filteredData = organisations
    // Exclude sub-organisations that have a parent_uuid
    .filter((item) => !item.parent_uuid)
    .filter((item) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        item.name?.toLowerCase().includes(query) ||
        item.org_number?.toLowerCase().includes(query) ||
        item.address_line1?.toLowerCase().includes(query) ||
        item.address_line2?.toLowerCase().includes(query) ||
        item.address_city?.toLowerCase().includes(query) ||
        item.country?.toLowerCase().includes(query)
      );
    })
    .map((item) => ({
      ...item,
      location: `${item.address_line1}, ${item.address_line2}`,
    }));

  const actionLoadOrganisations = React.useCallback(() => {
    apiGetAccountList(admin, globalAppMode)
      .then((response) => {
        const organisations: Organisation[] = response.data;
        setOrganisations(organisations);
      })
      .catch((error) => {
        const errorCode = error.response?.data?.error;
        console.log(errorCode);
      });
  }, [admin, globalAppMode]);

  useEffect(() => {
    actionLoadOrganisations();
  }, [actionLoadOrganisations]);

  return (
    <div className={cssLayout.layout}>
      <PageHeader
        title={
          globalAppMode === "HEALTH"
            ? _("organisation", "health_title")
            : _("organisation", "title")
        }
        subtitle={
          globalAppMode === "HEALTH"
            ? _("organisation", "health_subtitle")
            : _("organisation", "subtitle")
        }
      />
      <div className={css.contentBox}>
        <div className={css.searchRow}>
          <Search
            placeholder={_("general", "search")}
            value={searchQuery}
            onChange={setSearchQuery}
          />
          <FormButton
            label={_("organisation", "create_new")}
            onSubmit={handleCreateNew}
            inline={true}
            variant="contained"
            color="darkerGreen"
          />
        </div>
        <div className={css.tableBox}>
          <DataTable
            columns={columns}
            data={filteredData}
            onDelete={handleDelete}
            onRowClick={handleRowClick}
          />
        </div>
      </div>
      <ConfirmDialog
        open={deleteDialog.open}
        title={_("organisation", "confirm_delete_title")}
        message={_("organisation", "confirm_delete_message")}
        itemName={deleteDialog.item?.name}
        confirmLabel={_("organisation", "confirm_delete_yes")}
        cancelLabel={_("organisation", "cancel")}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteDialog({ open: false, item: null })}
      />
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default OrganisationScreen;
